import React, { useEffect, useRef } from "react"
import { Chart, ArcElement, Legend, PointStyle } from "chart.js"
import { SubSectorExposureData } from "../../api/quarterly_reports"

// Register Chart.js components
Chart.register(ArcElement, Legend)

interface SubSectorPieChartProps {
  data?: SubSectorExposureData[]
}

// Color palette for pie chart slices - based on BREE blue with variations
const CHART_COLORS = [
  "#143764", // BREE Blue (dark)
  "#1e4f8a", // BREE Blue (medium dark)
  "#2867b0", // BREE Blue (medium)
  "#4080c8", // BREE Blue (medium light)
  "#5899d6", // BREE Blue (light)
  "#70b2e4", // BREE Blue (lighter)
  "#88cbf2", // BREE Blue (very light)
  "#a0d8f8", // BREE Blue (pale)
  "#617491", // Grey Blue
  "#7a8ca8", // Medium Grey Blue
  "#93a4bf", // Light Grey Blue
  "#a6b3c4", // Lighter Grey Blue
  "#b9c6d9", // Very Light Grey Blue
  "#ccd9ee", // Pale Grey Blue
  "#405060", // Dark Grey Blue
  "#556678", // Medium Dark Grey Blue
  "#6a7c90", // Grey Blue Variant
  "#3a5570", // Dark Blue Grey
  "#507a9f", // Medium Blue Grey
  "#6690b4", // Light Blue Grey
]

const SubSectorPieChart: React.FC<SubSectorPieChartProps> = ({ data }) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart<"pie"> | null>(null)

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Filter out totals row and sort by qpcsp_weighting descending
    const filteredData = data
      .filter((item) => !item.is_total)
      .sort((a, b) => b.qpcsp_weighting - a.qpcsp_weighting)

    if (filteredData.length === 0) return

    // Show top 12, aggregate the rest into "Other"
    const MAX_SLICES = 12
    const topItems = filteredData.slice(0, MAX_SLICES)
    const remainingItems = filteredData.slice(MAX_SLICES)

    const chartData =
      remainingItems.length > 0
        ? [
            ...topItems,
            {
              sub_sector: `${remainingItems.length} Sub-Industries`,
              qpcsp_weighting: remainingItems.reduce(
                (sum, item) => sum + item.qpcsp_weighting,
                0,
              ),
              equity: 0,
              venture_capital: 0,
              real_assets: 0,
              credit: 0,
              is_total: false,
            },
          ]
        : topItems

    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
    }

    // Set higher device pixel ratio for crisp PDF rendering
    const dpr = Math.max(window.devicePixelRatio || 1, 2)
    Chart.defaults.devicePixelRatio = dpr

    // Create new chart
    const ctx = chartRef.current.getContext("2d")
    if (!ctx) return

    chartInstanceRef.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: chartData.map((item) => item.sub_sector),
        datasets: [
          {
            data: chartData.map((item) => item.qpcsp_weighting),
            backgroundColor: chartData.map(
              (_, index) => CHART_COLORS[index % CHART_COLORS.length],
            ),
            borderColor: "#FFFFFF",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        events: [], // Disable all interactivity (no tooltips, no hover states)
        layout: {
          padding: {
            right: 20, // Extra space for legend text
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "right",
            labels: {
              boxWidth: 15,
              boxHeight: 15,
              padding: 15,
              font: {
                size: 12,
              },
              color: "#000000", // Black text
              usePointStyle: true, // Use circles instead of squares
              pointStyle: "circle",
              generateLabels: (chart) => {
                const datasets = chart.data.datasets
                if (!datasets.length) return []

                const data = datasets[0].data as number[]
                const labels = chart.data.labels as string[]
                const backgroundColor = datasets[0].backgroundColor as string[]

                // Create labels sorted by value (descending) to match pie chart order
                return data
                  .map((value, index) => ({
                    text: `${labels[index]} (${value.toFixed(1)}%)`,
                    fillStyle: backgroundColor[index],
                    hidden: false,
                    index: index,
                    value: value,
                    pointStyle: "circle" as PointStyle,
                  }))
                  .sort((a, b) => {
                    const aIsOther = /^\d+ Sub-Industries/.test(a.text)
                    const bIsOther = /^\d+ Sub-Industries/.test(b.text)
                    if (aIsOther) return 1
                    if (bIsOther) return -1
                    return b.value - a.value
                  })
              },
            },
          },
          tooltip: {
            enabled: false, // Explicitly disable tooltips
          },
        },
      },
    })

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }
    }
  }, [data])

  return (
    <div className="sub-sector-pie-chart-container">
      <canvas ref={chartRef} />
    </div>
  )
}

export default SubSectorPieChart
